<template>
    <div class="data-table">
        <div v-if="loading">
            <spinner></spinner>
        </div>

        <div v-else class="table-responsive border mb-1" @scroll="handleTableScroll($event)">
            <div :class="{'table-scroller': scroll}">
                <table ref="containerRef" class="table relative table-striped table-hover">
                    <thead ref="theadRef">
                        <data-table-labels
                            v-if="labels"
                            :header="true"
                            :labels="labels"
                            :columns="columns">
                        </data-table-labels>
                        <data-table-header
                            @sort="sortTable"
                            :sortDirection="sort.direction"
                            :columns="columns">
                        </data-table-header>
                    </thead>
                    <tbody ref="tbodyRef">
                        <template v-for="row in rows" :key="row">
                            <data-table-row
                                :meta="meta"
                                :columns="columns"
                                :item="row">
                            </data-table-row>
                        </template>
                    </tbody>
                    <tfoot>
                        <data-table-header
                            @sort="sortTable"
                            :sortDirection="sort.direction"
                            :columns="columns">
                        </data-table-header>
                        <data-table-labels
                            v-if="labels"
                            :footer="true"
                            :labels="labels"
                            :columns="columns">
                        </data-table-labels>
                    </tfoot>
                </table>
            </div>
        </div>

        <br />

        <div v-cloak>
            <div v-if="!loading">
                <slot name="help-text"></slot>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick, inject } from 'vue';
import DataTableRow from './DataTableRow';
import DataTableHeader from './DataTableHeader';
import DataTableLabels from './DataTableLabels';

// Mixins
import { useStickyHeader } from '../DataTable/mixins/StickTableHeader';
import { useFlipcharts } from '../DataTable/mixins/FlipCharts';
import { useExportToCSV } from '../DataTable/mixins/ExportToCSV';
import { useSortTable } from '../DataTable/mixins/SortTable';

const props = defineProps({
    // An array of objects of data
    defaultRows: Array,
    // An array of objects expected in the format
    // [{field: 'symbol', label: 'Contract Name', sortable: Boolean}]
    defaultColumns: Array,
    // Default request parameters
    defaultRequest: Object,
    // Default sort parameters
    // {direction: 'asc', field: 'symbol'}
    defaultSort: Object,
    // Labels that appear above and below some column headers
    // [{label: '52 Week Low', classes: ['text-center'], columns: [4, 5, 6]}]
    defaultLabels: Array,
    defaultMeta: Object,
    // Whether this table should listen to scroll events
    scroll: {
        type: Boolean,
        default: true
    },
});

const eventBus = inject('eventBus');

const emit = defineEmits();

const sort = ref(props.defaultSort || {direction: 'asc', field: 'symbol'});
const request = ref(props.defaultRequest || {});
const meta = ref(props.defaultMeta || {});
const columns = ref(props.defaultColumns);
const loading = ref(request.value && Object.keys(request.value).length > 0 ? true : false);
const rows = ref(props.defaultRows || []);
const originalRows = ref(props.defaultRows || []);
const labels = ref(props.defaultLabels);

const { containerRef, theadRef, handleScroll, handleTableScroll, resizeHeader } = useStickyHeader();
const { exportToCSV } = useExportToCSV(rows, columns);
const { sortTable } = useSortTable(rows, sort);
const { flipcharts } = useFlipcharts(rows);

const requestData = () => {
    // console.log('DataTable: requestData');

    nextTick(() => {
        const route = request.value.route || window.route('api.data-table');
        loading.value = true;
        axios.post(route, request.value)
            .then((response) => {
                meta.value = response.data.data.meta || response.data.meta;
                rows.value = response.data.data.rows || response.data.data;

                if (typeof request.value.afterFunc === 'function') {
                    rows.value = request.value['afterFunc'](rows.value, request.value);
                }
            }).catch((error) => {
                console.error(error);
            }).then(() => {
                loading.value = false;
                eventBus.emit('data-loaded');
                emit('rows-loaded', rows.value);
                scrollToFromLocalStorage();
            });
    });
};

/**
* Attempts to scroll to the position the user was previously on when using the back/forward button
* Some pages are long and when clicking a `ButtonGroup` link and using the back button
* the position is stuck to the top of the page.
*/
const scrollToFromLocalStorage = () => {
    const scrollPosition = window.localStorage.getItem(`scroll-position-${window.location.pathname}`);

    // Only apply this when back/forward browser buttons are used
    if (window.performance && window.performance.navigation.type !== window.performance.navigation.TYPE_BACK_FORWARD) return;

    if (scrollPosition) {
        const yPosition = JSON.parse(scrollPosition).scrollY;

        nextTick(() => {
            window.scrollTo(0, yPosition);
        });

        window.localStorage.removeItem(`scroll-position-${window.location.pathname}`)
    }
};

// emit action
const timeframeChanged = (fields) => {
    const updatedFields = fields.map((mField) => mField.new).join();

   // Replace the field lookup on the columns
    columns.value = columns.value.map((mColumn) => {
        fields.map((field) => {
            if (mColumn.field === field.old) {
                mColumn.field = field.new;
            }
        });

        return mColumn;
    });

    request.value.fields = updatedFields;

    requestData();
};

// emit action
const showTriggered = (status) => {
    if (status) {
        originalRows.value = rows.value.map(row => Object.assign({}, row));
        const trendField = (columns.value.find(mColumn => mColumn.field.endsWith('InTrendSpotter')) || {}).field;
        const filteredRows = [];

        for (let i = 0; i < rows.value.length; i++) {
            const row = rows.value[i];

            if (((row.raw && row.raw[trendField] === 1) || row.highlight)) {
                let index = i;
                let group;

                do {
                    if (rows.value[index].level) {
                        group = rows.value[index];
                    }
                    index--;
                } while (index > 0 && !group);

                if (group && !filteredRows.includes(group)) {
                    filteredRows.push(group);
                }

                filteredRows.push(row);
            }
        }

        rows.value = filteredRows;
    } else {
         // This may need to be adjusted to reset rows
        rows.value = originalRows.value;
    }
};

// emit action
const requestChanged = (newSettings) => {
    // console.log('DataTable: requestChanged');
    if (newSettings.columns) {
        columns.value = newSettings.columns;
    }

    if (newSettings.labels !== undefined) {
        labels.value = newSettings.labels;
    }

    if (newSettings.request) {
        if (newSettings.request.merge) {
            request.value = Object.assign(request.value, newSettings.request);
            requestData();
        } else {
            request.value = newSettings.request;
        }
    }
};

// emit action
const sortBy = (requestParams) => {
    // console.log('DataTable: sortBy');
    let newRequest = Object.assign(request.value, requestParams);
    if (JSON.stringify(newRequest.value) !== JSON.stringify(request.value)) {
        request.value = newRequest;
        requestData();
    }
    // requestChanged(Object.assign(request.value, requestParams));
};

watch(() => props.defaultColumns, (newColumns) => {
    columns.value = newColumns;
}, { deep: true });

watch(() => props.defaultRequest, (newRequest) => {
    request.value = newRequest;
}, { deep: true });

// watch(() => request, () => {
//     console.log('watch request');
//     requestData();
// }, { deep: true, immediate: false });

watch(request, (newValue, oldValue) => {
    requestData();
}, { deep: true });

// Events from the GlobalEventBus to register
const globalEvents = [
    'flipcharts',
    'timeframeChanged',
    'requestChanged',
    'showTriggered',
    'exportToCSV',
    'sortBy', // Only used for the category/alpha "sorting"
    // 'periodChanged', // NOT USED
];

onMounted(() => {
    for (const eventName of globalEvents) {
        try {
            eventBus.on(eventName, eval(eventName));
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn(`Handler for ${eventName} not found.`);
            }
        }
    }

    if (request.value && Object.keys(request.value).length > 0) {
        requestData();
    }

    // Handled in mixins
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', resizeHeader);
});

onBeforeUnmount(() => {
    for (const eventName of globalEvents) {
        try {
            eventBus.off(eventName, eval(eventName));
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn(`Remove handler for ${eventName} not found.`);
            }
        }
    }

    // Handled in mixins
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', resizeHeader);
});
</script>
